import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { comics, getComicById } from '@/mocks/comics';
import ComicCard from '@/components/feature/ComicCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { useSeo } from '@/hooks/useSeo';

function buildChapters(id: string, total: number) {
  const count = Math.min(18, total);
  return Array.from({ length: count }).map((_, i) => ({
    no: total - i,
    title: `第${total - i}话 · 章节内容`,
    isNew: i === 0,
  }));
}

export default function ComicDetail() {
  const { id } = useParams<{ id: string }>();
  const comic = getComicById(id || '');

  useSeo({
    title: comic
      ? `${comic.title}抢先看最新话 - 同步更新进度与汉化进度 | 策驰漫画网`
      : '漫画详情 - 策驰漫画网',
    description: comic
      ? `${comic.title}是策驰漫画网热门${comic.categories.join('/')}连载，抢先看最新话，同步更新进度${comic.updateProgress}%，汉化进度${comic.translateProgress}%，追更不再错过。`
      : '策驰漫画网漫画详情页',
    keywords: `策驰漫画网,${comic?.title ?? ''},抢先看最新话,同步更新进度,汉化进度`,
    type: 'article',
  });

  const chapters = useMemo(
    () => (comic ? buildChapters(comic.id, comic.totalChapters) : []),
    [comic],
  );

  if (!comic) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-32 text-center md:px-6">
        <h1 className="font-heading text-3xl text-foreground-950">未找到该漫画</h1>
        <Link
          to="/comics"
          className="mt-4 inline-flex whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-bold text-background-50 hover:bg-primary-600"
        >
          返回漫画库
        </Link>
      </div>
    );
  }

  const related = comics.filter((c) => c.id !== comic.id && c.categories.some((x) => comic.categories.includes(x))).slice(0, 4);
  const relatedList = related.length >= 4 ? related : [...related, ...comics.filter((c) => c.id !== comic.id && !related.includes(c))].slice(0, 4);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://gifcart.github.io/' },
          { '@type': 'ListItem', position: 2, name: '漫画库', item: 'https://gifcart.github.io/comics' },
          { '@type': 'ListItem', position: 3, name: comic.title },
        ],
      },
      {
        '@type': 'CreativeWork',
        name: comic.title,
        about: comic.description,
        genre: comic.categories.join(','),
        author: { '@type': 'Organization', name: comic.author },
        url: `https://gifcart.github.io/comic/${comic.id}`,
      },
    ],
  };

  return (
    <div className="mx-auto max-w-7xl px-4 pb-20 pt-28 md:px-6 md:pt-32">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '漫画库', to: '/comics' }, { label: comic.title }]} />

      <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-3">
        <div className="lg:col-span-1">
          <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
            <div className="relative aspect-[3/4] w-full overflow-hidden">
              <img
                src={comic.cover}
                alt={`${comic.title} 漫画封面`}
                title={`${comic.title} 抢先看与同步更新`}
                className="h-full w-full object-cover object-top"
              />
            </div>
            <div className="p-4">
              <div className="flex items-center justify-between">
                <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${comic.status === '连载中' ? 'bg-secondary-100 text-secondary-700' : 'bg-accent-100 text-accent-800'}`}>
                  {comic.status}
                </span>
                <span className="flex items-center gap-1 text-sm font-bold text-accent-600">
                  <i className="ri-star-fill"></i>{comic.rating}
                </span>
              </div>
              <div className="mt-4 space-y-4">
                <div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-foreground-600">同步更新进度</span>
                    <span className="font-bold text-secondary-700">{comic.updateProgress}%</span>
                  </div>
                  <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-background-200">
                    <div className="h-full rounded-full bg-secondary-500" style={{ width: `${comic.updateProgress}%` }}></div>
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-foreground-600">汉化进度</span>
                    <span className="font-bold text-primary-700">{comic.translateProgress}%</span>
                  </div>
                  <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-background-200">
                    <div className="h-full rounded-full bg-primary-500" style={{ width: `${comic.translateProgress}%` }}></div>
                  </div>
                </div>
              </div>
              <div className="mt-5 grid grid-cols-2 gap-2">
                <button
                  type="button"
                  className="flex items-center justify-center whitespace-nowrap rounded-full bg-primary-500 px-4 py-2.5 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-book-open-line mr-1.5"></i>开始阅读
                </button>
                <button
                  type="button"
                  className="flex items-center justify-center whitespace-nowrap rounded-full border border-primary-200 px-4 py-2.5 text-sm font-bold text-primary-600 transition-colors hover:bg-primary-50"
                >
                  <i className="ri-heart-add-line mr-1.5"></i>关注追更
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-2">
          <h1 className="font-heading text-2xl leading-tight text-foreground-950 md:text-4xl">
            {comic.title}
          </h1>
          <div className="mt-3 flex flex-wrap gap-2">
            {comic.categories.map((c) => (
              <Link
                key={c}
                to={`/comics?category=${encodeURIComponent(c)}`}
                className="rounded-full bg-primary-50 px-3 py-1 text-xs font-medium text-primary-700 transition-colors hover:bg-primary-100"
              >
                {c}
              </Link>
            ))}
            {comic.tags.map((t) => (
              <span key={t} className="rounded-full bg-background-100 px-3 py-1 text-xs text-foreground-600">
                #{t}
              </span>
            ))}
          </div>

          <dl className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
            {[
              { k: '作者', v: comic.author },
              { k: '更新', v: comic.updateDay },
              { k: '总话数', v: `${comic.totalChapters} 话` },
              { k: '人气', v: comic.views },
            ].map((item) => (
              <div key={item.k} className="rounded-lg border border-background-200 bg-background-50 p-3 text-center">
                <dt className="text-xs text-foreground-500">{item.k}</dt>
                <dd className="mt-1 truncate text-sm font-bold text-foreground-950">{item.v}</dd>
              </div>
            ))}
          </dl>

          <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="text-sm font-bold text-foreground-950">作品简介</h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-700">{comic.description}</p>
            <p className="mt-3 text-sm leading-relaxed text-foreground-700">
              最新话「{comic.latestChapter}」已抢先上线，同步更新进度已达 {comic.updateProgress}%，
              汉化进度 {comic.translateProgress}%，追更的读者可以放心进入阅读，不再错过任何一话。
            </p>
          </div>

          <div className="mt-8">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-xl text-foreground-950">章节列表</h2>
              <span className="text-xs text-foreground-500">共 {comic.totalChapters} 话 · 最近更新</span>
            </div>
            <ul className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
              {chapters.map((ch) => (
                <li key={ch.no}>
                  <div className="flex items-center justify-between rounded-md border border-background-200 bg-background-50 px-4 py-3 transition-colors hover:border-primary-200 hover:bg-primary-50">
                    <span className="text-sm text-foreground-800">{ch.title}</span>
                    {ch.isNew && (
                      <span className="whitespace-nowrap rounded-full bg-primary-500 px-2 py-0.5 text-[11px] font-bold text-background-50">
                        抢先看
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <section className="mt-16">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-2xl text-foreground-950">相关推荐</h2>
          <Link to="/comics" className="flex items-center text-sm font-medium text-primary-600 hover:text-primary-700">
            更多漫画 <i className="ri-arrow-right-line ml-1"></i>
          </Link>
        </div>
        <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {relatedList.map((c) => (
            <ComicCard key={c.id} comic={c} />
          ))}
        </div>
      </section>
    </div>
  );
}